import type { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';
import { forwardRef } from 'react';
import { styled, css } from 'styled-components';
import { isNotNil } from '@wistia/type-guards';
import type { ColorSchemeTypes } from '../ColorSchemeWrapper';
import { ellipsisStyle } from '../Ellipsis/Ellipsis';
import { lineClampCss } from '../../css/lineClampCss';
import { getColorScheme } from '../../private/helpers/getColorScheme';
import { makePolymorphic } from '../../private/helpers/makePolymorphic';
import { applyMaxCharsToChildren } from '../../private/helpers/applyMaxCharsToChildren';
import { iconInlineStyle } from '../Icon/Icon';

type AlignmentTypes = 'center' | 'justify' | 'left' | 'right';

const heroStyle = css`
  --font-family: var(--wui-typography-heading-hero-family);
  --font-size: var(--wui-typography-heading-hero-size);
  --font-weight: var(--wui-typography-heading-hero-weight);
  --line-height: var(--wui-typography-heading-hero-line-height);
`;

const heading1TextStyle = css`
  --font-family: var(--wui-typography-heading-1-family);
  --font-size: var(--wui-typography-heading-1-size);
  --font-weight: var(--wui-typography-heading-1-weight);
  --line-height: var(--wui-typography-heading-1-line-height);
`;

const heading2TextStyle = css`
  --font-family: var(--wui-typography-heading-2-family);
  --font-size: var(--wui-typography-heading-2-size);
  --font-weight: var(--wui-typography-heading-2-weight);
  --line-height: var(--wui-typography-heading-2-line-height);
`;

const heading3TextStyle = css`
  --font-family: var(--wui-typography-heading-3-family);
  --font-size: var(--wui-typography-heading-3-size);
  --font-weight: var(--wui-typography-heading-3-weight);
  --line-height: var(--wui-typography-heading-3-line-height);
`;

const heading4TextStyle = css`
  --font-family: var(--wui-typography-heading-4-family);
  --font-size: var(--wui-typography-heading-4-size);
  --font-weight: var(--wui-typography-heading-4-weight);
  --line-height: var(--wui-typography-heading-4-line-height);
`;

const heading5TextStyle = css`
  --font-family: var(--wui-typography-heading-5-family);
  --font-size: var(--wui-typography-heading-5-size);
  --font-weight: var(--wui-typography-heading-5-weight);
  --line-height: var(--wui-typography-heading-5-line-height);
`;

const heading6TextStyle = css`
  --font-family: var(--wui-typography-heading-6-family);
  --font-size: var(--wui-typography-heading-6-size);
  --font-weight: var(--wui-typography-heading-6-weight);
  --line-height: var(--wui-typography-heading-6-line-height);
`;

export const variantStyleMap = {
  hero: heroStyle,
  heading1: heading1TextStyle,
  heading2: heading2TextStyle,
  heading3: heading3TextStyle,
  heading4: heading4TextStyle,
  heading5: heading5TextStyle,
  heading6: heading6TextStyle,
};

const DEFAULT_ELEMENT = 'h1';

export type HeadingProps = ComponentPropsWithoutRef<typeof DEFAULT_ELEMENT> & {
  /**
   * The horizontal alignment
   * <br />
   * _Note: this only affects block elements_
   */
  align?: AlignmentTypes;
  /**
   * The content of the heading
   */
  children: ReactNode;

  /**
   * Sets the [color scheme](../?path=/docs/docs-color-schemes--docs)
   */
  colorScheme?: ColorSchemeTypes;
  /**
   * Styles to text as disabled
   */
  disabled?: boolean;
  /**
   * Display the text as inline content
   */
  inline?: boolean;
  /**
   * Maximum number of characters to display before truncating. Must be >= 1.
   * When truncated, an ellipsis (...) will be added to the end.
   */
  maxChars?: number;
  /**
   * Text prominence affects color. Use 'primary' for main non-interactive text, 'secondary' for supporting text, and 'button' in specific cases where text is used in an interactive component.
   */
  prominence?: 'button' | 'primary' | 'secondary';
  /**
   * Prevents text from being highlighted and copied
   */
  preventUserSelect?: boolean;
  // This will inherit the jsdoc block from PolymorphicComponent
  renderAs?: ElementType;
  /**
   * Number of lines to display before truncating with an ellipsis.
   * Set to 1 to truncate after a single line, 2 for two lines, etc.
   * <br />
   * _Note: this only affects block elements_
   */
  maxLines?: number;
  /**
   * The text style to display. This will also affect what element (h1, h2, etc) is rendered, unless the `renderAs` prop is used
   */
  variant?: keyof typeof variantStyleMap;
};

type StyledHeadingProps = {
  $align: AlignmentTypes;
  $colorScheme: ColorSchemeTypes;
  $disabled: boolean;
  $inline: boolean;
  $preventUserSelect: boolean;
  $prominence: 'button' | 'primary' | 'secondary';
  $maxLines: number | undefined;
  $variant: keyof typeof variantStyleMap;
  as?: ElementType;
};

const StyledHeading = styled.div<StyledHeadingProps>`
  ${({ $colorScheme }) => getColorScheme($colorScheme)};
  --text-color: ${({ $prominence, $disabled }) => {
    if ($disabled) {
      return 'var(--wui-color-text-disabled)';
    }
    if ($prominence === 'secondary') {
      return 'var(--wui-color-text-secondary)';
    }
    if ($prominence === 'button') {
      return 'var(--wui-color-text-button)';
    }
    return 'var(--wui-color-text)';
  }};

  font-family: var(--font-family);
  font-size: var(--font-size);
  font-weight: var(--font-weight);
  line-height: var(--line-height);
  color: var(--text-color);
  ${({ $variant }) => variantStyleMap[$variant]}
  ${({ $maxLines }) => {
    if (isNotNil($maxLines)) {
      return css`
        ${ellipsisStyle};
        ${lineClampCss($maxLines)};
      `;
    }
    return undefined;
  }}
  ${({ $inline }) =>
    $inline &&
    css`
      display: inline-block;
    `}
  ${({ $preventUserSelect }) =>
    $preventUserSelect &&
    css`
      user-select: none;
    `}
  ${({ $align }) => css`
    text-align: ${$align};
  `}
  margin: 0;

  ${iconInlineStyle}
`;

export const variantElementMap: Record<string, ElementType> = {
  hero: DEFAULT_ELEMENT,
  heading1: DEFAULT_ELEMENT,
  heading2: 'h2',
  heading3: 'h3',
  heading4: 'h4',
  heading5: 'h5',
  heading6: 'h6',
};

const HeadingComponent = forwardRef<typeof DEFAULT_ELEMENT, HeadingProps>(
  (
    {
      align = 'left',
      children,
      colorScheme = 'inherit',
      disabled = false,
      inline = false,
      maxChars,
      preventUserSelect = false,
      prominence = 'primary',
      maxLines,
      variant = 'heading1',
      renderAs,
      ...props
    },
    ref,
  ) => {
    const processedChildren = applyMaxCharsToChildren(children, maxChars);

    return (
      <StyledHeading
        ref={ref}
        $align={align}
        $colorScheme={colorScheme}
        $disabled={disabled}
        $inline={inline}
        $maxLines={maxLines}
        $preventUserSelect={preventUserSelect}
        $prominence={prominence}
        $variant={variant}
        as={renderAs ?? variantElementMap[variant] ?? DEFAULT_ELEMENT}
        {...props}
      >
        {processedChildren}
      </StyledHeading>
    );
  },
);

HeadingComponent.displayName = 'Heading';

/**
 * Displaying heading text, both visually and semantically
 */
// @ts-expect-error makePolymorphic is causing issues with types in R19
export const Heading = makePolymorphic<typeof DEFAULT_ELEMENT, HeadingProps>(HeadingComponent);
